/**
 * Expense-date ranges for the Expense Reports filters and report titles.
 * Dates are ISO strings (YYYY-MM-DD) in the viewer's time zone; a range is
 * { key, from, to, label } with null for an open end.
 */

const pad = (n) => String(n).padStart(2, '0');

/** A Date as YYYY-MM-DD in the viewer's time zone. */
export const isoOf = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** "2026-01-31" -> "01/31/2026". */
export const usDate = (iso) => {
  const [year, month, day] = String(iso).split('-');
  return `${month}/${day}/${year}`;
};

export const DEFAULT_RANGE_KEY = 'ytd';
export const PREVIOUS_YEARS = 4;

/** The choices in the range picker: this year, the four before it, all dates, custom. */
export const presetsFor = (today = new Date()) => {
  const year = today.getFullYear();
  return [
    { key: 'ytd', label: `This year (${year})` },
    ...Array.from({ length: PREVIOUS_YEARS }, (_, i) => ({ key: `year:${year - i - 1}`, label: String(year - i - 1) })),
    { key: 'all', label: 'All dates' },
    { key: 'custom', label: 'Custom range…' },
  ];
};

/**
 * The range for a picker choice, or { error } for an unusable custom range.
 * custom: { from, to } as ISO strings, either may be empty.
 */
export const rangeFor = (key, custom = {}, today = new Date()) => {
  if (key === 'all') return { key, from: null, to: null, label: 'All expense dates' };
  if (key === 'ytd') {
    const from = `${today.getFullYear()}-01-01`;
    const to = isoOf(today);
    return { key, from, to, label: `Expense dates ${usDate(from)} – ${usDate(to)}` };
  }
  if (key.startsWith('year:')) {
    const year = key.slice(5);
    return { key, from: `${year}-01-01`, to: `${year}-12-31`, label: `Calendar year ${year}` };
  }
  const from = custom.from || null;
  const to = custom.to || null;
  if (!from && !to) return { error: 'Choose a start date, an end date, or both.' };
  if (from && to && from > to) return { error: 'The start date must be on or before the end date.' };
  let label;
  if (from && to) label = `Expense dates ${usDate(from)} – ${usDate(to)}`;
  else if (from) label = `Expense dates from ${usDate(from)}`;
  else label = `Expense dates through ${usDate(to)}`;
  return { key: 'custom', from, to, label };
};

/** Whether an expense's date falls in the range (inclusive). */
export const inRange = (expense, range) =>
  (!range.from || expense.expense_date >= range.from) && (!range.to || expense.expense_date <= range.to);

/** For file names: "2026-01-01_to_2026-11-02", "all-dates". */
export const rangeSlug = (range) => {
  if (!range.from && !range.to) return 'all-dates';
  return `${range.from || 'start'}_to_${range.to || 'latest'}`;
};
